import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NewsCard from '@/components/feature/NewsCard';
import ShareDialog from './components/ShareDialog';
import { formatViews, getCategoryById, getNewsById, getRelatedNews } from '@/utils/newsUtils';
import { useSeo } from '@/hooks/useSeo';

const FAV_KEY = 'chigua_favorites';

function readFavorites(): string[] {
  try {
    const raw = localStorage.getItem(FAV_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

export default function Detail() {
  const { newsId } = useParams();
  const article = getNewsById(newsId);
  const category = article ? getCategoryById(article.categoryId) : undefined;
  const [shareOpen, setShareOpen] = useState(false);
  const [favorites, setFavorites] = useState<string[]>(readFavorites);

  useSeo({
    title: article ? `${article.title} - 吃瓜网新闻详情` : '新闻未找到 - 吃瓜网',
    description: article
      ? `${article.summary}（吃瓜网${category ? category.name : '热点'}频道资讯）`
      : '该新闻内容已下线或不存在，请返回吃瓜网首页浏览其他热点资讯。',
    keywords: article
      ? `${article.tags.join(',')},吃瓜网,${category ? category.name : '热点新闻'}`
      : '吃瓜网,热点新闻',
    canonicalPath: `/detail/${newsId}`,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [newsId]);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 w-full flex items-center justify-center px-4 py-20">
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-8 py-16 text-center max-w-lg">
            <i className="ri-file-forbid-line text-4xl text-foreground-300"></i>
            <p className="mt-4 text-lg font-medium text-foreground-800">新闻内容已下线或不存在</p>
            <p className="mt-2 text-sm text-foreground-500">也许它已经被移除，去看看其他热点吧</p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-primary-500 text-primary-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const isFavorite = favorites.includes(article.id);
  const related = getRelatedNews(article, 3);

  const toggleFavorite = () => {
    setFavorites((prev) => {
      const next = prev.includes(article.id) ? prev.filter((x) => x !== article.id) : [...prev, article.id];
      try {
        localStorage.setItem(FAV_KEY, JSON.stringify(next));
      } catch {
        /* ignore storage errors */
      }
      return next;
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1 w-full">
        <div className="w-full px-4 md:px-8 py-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              category ? { label: category.name, to: `/category/${category.id}` } : { label: '资讯' },
              { label: article.title },
            ]}
          />

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <article className="lg:col-span-8">
              {category && (
                <Link
                  to={`/category/${category.id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={category.icon}></i>
                  {category.name}
                </Link>
              )}

              <h1 className="mt-3 text-2xl md:text-4xl font-bold font-heading text-foreground-950 leading-snug">
                {article.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.publishedAt}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {formatViews(article.views)}
                </span>
                <button
                  type="button"
                  onClick={toggleFavorite}
                  className={`ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    isFavorite
                      ? 'bg-primary-500 text-primary-50'
                      : 'border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  <i className={isFavorite ? 'ri-bookmark-fill' : 'ri-bookmark-line'}></i>
                  {isFavorite ? '已收藏' : '收藏'}
                </button>
              </div>

              <p className="mt-3 text-xs text-foreground-400">收藏为本地演示功能，仅保存在当前浏览器，不会同步到服务器。</p>

              <div className="mt-6 w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-100">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={`${article.title} - 吃瓜网新闻详情`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="mt-8 max-w-3xl">
                {article.content.map((block, i) => {
                  if (block.type === 'heading') {
                    return (
                      <h3 key={i} className="mt-8 mb-3 text-lg md:text-xl font-bold font-heading text-foreground-950">
                        {block.text}
                      </h3>
                    );
                  }
                  if (block.type === 'image') {
                    return (
                      <figure key={i} className="my-6">
                        <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-100">
                          <img
                            src={block.src}
                            alt={block.caption || article.title}
                            title={`${article.title} - 吃瓜网图片`}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        {block.caption && (
                          <figcaption className="mt-2 text-center text-xs text-foreground-400">{block.caption}</figcaption>
                        )}
                      </figure>
                    );
                  }
                  return (
                    <p key={i} className="mb-4 text-base text-foreground-800 leading-loose">
                      {block.text}
                    </p>
                  );
                })}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-800 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-price-tag-3-line"></i>
                    {tag}
                  </Link>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
                <span className="text-sm font-medium text-foreground-800">觉得有意思？</span>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary-500 text-primary-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-line"></i>
                  分享资讯
                </button>
                {category && (
                  <Link
                    to={`/category/${category.id}`}
                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-background-50 border border-background-300 text-foreground-800 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    进入「{category.name}」频道
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                )}
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <h2 className="text-lg font-bold font-heading text-foreground-950">相关推荐</h2>
                <div className="mt-4 flex flex-col gap-4">
                  {related.length === 0 ? (
                    <p className="text-sm text-foreground-500">暂无相关推荐</p>
                  ) : (
                    related.map((item) => <NewsCard key={item.id} article={item} layout="column" showSummary={false} />)
                  )}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} title={article.title} />
      <SiteFooter />
    </div>
  );
}